import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { ENV } from 'shared/ENV';
import { AircraftIconAtlas, buildAircraftIconAtlas } from './aircraftIconAtlas';

// Debugging util to log info about the atlas and a URL for direct inspection.
const logAtlasSummary = (atlas: AircraftIconAtlas): void => {
  if (ENV.PROD) {
    return;
  }
  const keys = Object.keys(atlas.mapping);
  /* eslint-disable no-console */
  console.log('[aircraft atlas] cells drawn:', {
    mappedKeys: keys.length,
    bodyCells: keys.filter((key) => key.endsWith('-body')).length,
    rotorCells: keys.filter((key) => /-rotor\d+$/.test(key)).length,
    rotorManifest: Object.keys(atlas.rotors),
    textureBytes: atlas.atlasUrl.length,
  });
  console.log('[aircraft atlas] open to inspect:', atlas.atlasUrl);
  /* eslint-enable no-console */
};

let atlasPromise: Promise<AircraftIconAtlas> | null = null;

/**
 * Builds the atlas if needed and returns it.
 */
export const getAircraftIconAtlas = (): Promise<AircraftIconAtlas> => {
  if (!atlasPromise) {
    atlasPromise = buildAircraftIconAtlas().catch((error: unknown) => {
      // Let a later mount retry rather than caching the rejection forever.
      atlasPromise = null;
      throw error;
    });
  }
  return atlasPromise;
};

/**
 * Returns the built icon atlas if available, or null if either it is being built or
 * if `enabled` is false.
 */
export const useAircraftIconAtlas = (
  enabled: boolean,
): AircraftIconAtlas | null => {
  const [atlas, setAtlas] = useState<AircraftIconAtlas | null>(null);

  useEffect(() => {
    if (!enabled || atlas) {
      // Don't do work if atlas already set, or if not enabled.
      return undefined;
    }

    let cancelled = false;
    getAircraftIconAtlas()
      .then((result) => {
        if (!cancelled) {
          logAtlasSummary(result);
          setAtlas(result);
        }
        return result;
      })
      .catch((error: unknown) => {
        // Surfaced locally as well as to Sentry: a failure here renders no
        // aircraft at all, and it is otherwise completely silent in dev.
        console.error('Failed to build the aircraft icon atlas', error);
        Sentry.captureException(error, {
          tags: { scope: 'aircraftIconAtlas' },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, atlas]);

  return atlas;
};
